import type { ClientResource, WebhookDeliveryResource } from '@voxler/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useToast } from '../components/toast';
import { internalFetch, internalSend } from '../lib/api';
import { PageChrome } from '@/components/page-chrome';
import { Callout } from '@/components/ui/alert';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardBar, CardContent, CardHead } from '@/components/ui/card';
import { ChartLegend, TimeChart, type BarSeries, type ChartColor } from '@/components/ui/chart';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { Row4 } from '@/components/ui/layout-grid';
import { PageHead, formatMonthLabel } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import { StatCard } from '@/components/ui/stat-card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  rowLinkMonoClass,
} from '@/components/ui/table';
import { TableTabs, type TableTabItem } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const dateFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

/** Calendar day (YYYY-MM-DD) in the business time zone, for the per-day chart. */
const parisDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const ALL_CLIENTS = 'all';
const NO_WEBHOOK_CAUSE = 'no_webhook_configured';
const SERIES_COLORS: readonly ChartColor[] = ['chart-1', 'chart-2', 'chart-3', 'chart-4'];

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : dateFormatter.format(date);
}

function parisDay(iso: string): string | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : parisDayFormatter.format(date);
}

function statusVariant(status: WebhookDeliveryResource['status']): BadgeVariant {
  switch (status) {
    case 'delivered':
      return 'success';
    case 'pending':
      return 'info';
    case 'failed':
      return 'danger';
  }
}

function statusLabel(status: WebhookDeliveryResource['status']): string {
  switch (status) {
    case 'delivered':
      return 'Livrée';
    case 'pending':
      return 'En attente';
    case 'failed':
      return 'Échec';
  }
}

function clientLabel(delivery: WebhookDeliveryResource): string {
  return delivery.clientName ?? delivery.clientId;
}

function shortRef(ref: string): string {
  return ref.length > 18 ? `${ref.slice(0, 18)}…` : ref;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count > 1 ? pluralForm : singular;
}

interface DailyChart {
  month: string;
  categories: string[];
  bars: BarSeries[];
  total: number;
}

/** Failures per day of the current month (Europe/Paris), stacked by client (top 4 + others). */
function buildDailyChart(rows: readonly WebhookDeliveryResource[]): DailyChart | null {
  const today = parisDayFormatter.format(new Date());
  const month = today.slice(0, 7);
  const lastDay = Number(today.slice(8, 10));
  const inMonth = rows.flatMap((row) => {
    const day = parisDay(row.createdAt);
    return day !== null && day.startsWith(month) ? [{ row, day: Number(day.slice(8, 10)) }] : [];
  });
  if (inMonth.length === 0) {
    return null;
  }
  const byClient = new Map<string, { label: string; values: number[] }>();
  for (const { row, day } of inMonth) {
    const entry = byClient.get(row.clientId) ?? {
      label: clientLabel(row),
      values: Array.from({ length: lastDay }, () => 0),
    };
    entry.values[day - 1] = (entry.values[day - 1] ?? 0) + 1;
    byClient.set(row.clientId, entry);
  }
  const sum = (values: readonly number[]): number => values.reduce((acc, value) => acc + value, 0);
  const ranked = [...byClient.entries()].sort((a, b) => sum(b[1].values) - sum(a[1].values));
  const bars: BarSeries[] = [];
  const others = Array.from({ length: lastDay }, () => 0);
  let otherCount = 0;
  for (const [index, [clientId, entry]] of ranked.entries()) {
    const color = SERIES_COLORS[index];
    if (color !== undefined) {
      bars.push({ key: clientId, label: entry.label, color, values: entry.values });
      continue;
    }
    otherCount += 1;
    entry.values.forEach((value, day) => {
      others[day] = (others[day] ?? 0) + value;
    });
  }
  if (otherCount > 0) {
    bars.push({ key: 'others', label: 'Autres clients', color: 'chart-5', values: others });
  }
  return {
    month,
    categories: Array.from({ length: lastDay }, (_, index) => String(index + 1)),
    bars,
    total: inMonth.length,
  };
}

export function WebhooksPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [clientTab, setClientTab] = useState<string>(ALL_CLIENTS);

  const deliveries = useQuery({
    queryKey: ['webhook-deliveries'],
    queryFn: () =>
      internalFetch<{ deliveries: WebhookDeliveryResource[] }>('/internal/webhooks/deliveries'),
  });
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });

  const replay = useMutation({
    mutationFn: (id: string) => internalSend('POST', `/internal/webhooks/deliveries/${id}/replay`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webhook-deliveries'] });
      notify('Livraison renvoyée');
    },
  });

  const rows = useMemo(() => deliveries.data?.deliveries ?? [], [deliveries.data]);

  const stats = useMemo(() => {
    const failed = rows.filter((row) => row.status === 'failed');
    const causes = new Set(
      failed.flatMap((row) => (row.lastError === null ? [] : [row.lastError])),
    );
    const attempts = new Set(failed.map((row) => row.attempts));
    const clientIds = new Set(rows.map((row) => row.clientId));
    return {
      failed: failed.length,
      causes: [...causes],
      attempts: [...attempts],
      totalAttempts: failed.reduce((acc, row) => acc + row.attempts, 0),
      withoutCause: failed.filter((row) => row.lastError === null).length,
      clients: clientIds.size,
    };
  }, [rows]);

  const tabs = useMemo((): TableTabItem<string>[] => {
    const byClient = new Map<string, { label: string; count: number }>();
    for (const row of rows) {
      const entry = byClient.get(row.clientId) ?? { label: clientLabel(row), count: 0 };
      entry.count += 1;
      byClient.set(row.clientId, entry);
    }
    return [
      { value: ALL_CLIENTS, label: 'Tous les clients', count: rows.length },
      ...[...byClient.entries()]
        .sort((a, b) => b[1].count - a[1].count)
        .map(([clientId, entry]) => ({ value: clientId, label: entry.label, count: entry.count })),
    ];
  }, [rows]);

  const activeTab = tabs.some((tab) => tab.value === clientTab) ? clientTab : ALL_CLIENTS;
  const filtered = useMemo(
    () => (activeTab === ALL_CLIENTS ? rows : rows.filter((row) => row.clientId === activeTab)),
    [rows, activeTab],
  );
  const pagination = usePagination(filtered, 10);
  const chart = useMemo(() => buildDailyChart(rows), [rows]);

  const onlyCause = stats.causes.length === 1 ? stats.causes[0] : undefined;
  const clientTotal = clients.data?.clients.length;

  return (
    <>
      <PageChrome crumbs={[{ label: 'Webhooks' }]} />
      <PageHead title="Webhooks" subtitle="Livraisons en échec et rejeu" />

      {onlyCause !== undefined && stats.failed > 1 && stats.withoutCause === 0 ? (
        <Callout
          role="status"
          title={
            <>
              Les {stats.failed} échecs ont la même cause :{' '}
              <span className="font-mono">{onlyCause}</span>
            </>
          }
          action={
            onlyCause === NO_WEBHOOK_CAUSE ? (
              <Link to="/clients" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                Configurer un client
              </Link>
            ) : undefined
          }
        >
          {onlyCause === NO_WEBHOOK_CAUSE
            ? "Aucun webhook n'est configuré : rejouer maintenant échouera de nouveau. Configurer l'URL de chaque client, puis rejouer."
            : 'Rejouer avant de corriger cette cause échouera probablement de nouveau.'}
        </Callout>
      ) : null}

      {deliveries.isSuccess ? (
        <Row4 aria-label="Indicateurs des livraisons">
          <StatCard
            label="Livraisons en échec"
            value={String(stats.failed)}
            foot={
              stats.failed === 0
                ? 'aucune à rejouer'
                : stats.attempts.length === 1 && stats.failed > 1
                  ? `toutes à ${stats.attempts[0] ?? 0} ${plural(stats.attempts[0] ?? 0, 'tentative', 'tentatives')}`
                  : `${stats.totalAttempts} ${plural(stats.totalAttempts, 'tentative', 'tentatives')} au total`
            }
          />
          <StatCard
            label="Causes distinctes"
            value={String(stats.causes.length)}
            foot={
              onlyCause ?? (stats.causes.length === 0 ? 'aucune erreur enregistrée' : undefined)
            }
            footMono={onlyCause !== undefined}
          />
          <StatCard
            label="Clients concernés"
            value={String(stats.clients)}
            foot={clientTotal !== undefined ? `sur ${clientTotal} clients` : undefined}
          />
        </Row4>
      ) : null}

      {chart !== null ? (
        <Card>
          <CardHead
            title="Échecs par jour"
            description={`${formatMonthLabel(chart.month)}, par client`}
            actions={
              chart.bars.length > 1 ? (
                <ChartLegend
                  items={chart.bars.map((bar) => ({ label: bar.label, color: bar.color }))}
                />
              ) : undefined
            }
          />
          <CardContent className="grid gap-3">
            <TimeChart
              categories={chart.categories}
              bars={chart.bars}
              stacked
              every={1}
              ticks={4}
              maxBar={36}
              height={200}
              width={1100}
              padLeft={36}
              ariaLabel={`Échecs de livraison par jour en ${formatMonthLabel(chart.month)} : ${chart.total} au total, ${chart.bars
                .map(
                  (bar) => `${bar.values.reduce((acc, value) => acc + value, 0)} pour ${bar.label}`,
                )
                .join(', ')}.`}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardBar
          start={
            <TableTabs
              aria-label="Filtrer par client"
              value={activeTab}
              onValueChange={(value) => {
                setClientTab(value);
                pagination.setPage(1);
              }}
              items={tabs}
            />
          }
        />
        {deliveries.isLoading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Aucune livraison en échec"
            description="Une livraison de webhook qui échoue apparaît ici avec sa cause, prête à être rejouée."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Événement</TableHead>
                  <TableHead>Requête</TableHead>
                  <TableHead kind="num">Tentatives</TableHead>
                  <TableHead kind="num">Dernier statut HTTP</TableHead>
                  <TableHead>Dernière erreur</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead kind="act">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((delivery) => {
                  const replaying = replay.isPending && replay.variables === delivery.id;
                  return (
                    <TableRow key={delivery.id}>
                      <TableCell kind="mono">{formatDate(delivery.createdAt)}</TableCell>
                      <TableCell kind="name">{clientLabel(delivery)}</TableCell>
                      <TableCell>
                        <Tag mono>{delivery.eventType}</Tag>
                      </TableCell>
                      <TableCell>
                        {delivery.requestId !== null ? (
                          <Link
                            to="/requests/$requestId"
                            params={{ requestId: delivery.requestId }}
                            className={rowLinkMonoClass}
                            title={delivery.requestId}
                          >
                            {shortRef(delivery.requestId)}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell kind="num">{delivery.attempts}</TableCell>
                      <TableCell kind="num">{delivery.lastResponseStatus ?? '—'}</TableCell>
                      <TableCell
                        kind="mono"
                        className="max-w-[280px] truncate text-xs"
                        title={delivery.lastError ?? ''}
                      >
                        {delivery.lastError ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(delivery.status)}>
                          {statusLabel(delivery.status)}
                        </Badge>
                      </TableCell>
                      <TableCell kind="act">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Rejouer la livraison ${delivery.id}`}
                              onClick={() => {
                                replay.mutate(delivery.id);
                              }}
                              disabled={replaying}
                            >
                              <RefreshCw aria-hidden="true" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Rejouer</TooltipContent>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pager
              itemLabel="livraisons"
              {...pagination.pagerProps}
              pageSizeOptions={[10, 25, 50]}
            />
          </>
        )}
      </Card>
    </>
  );
}
